'use client';
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CandidateDateRange } from '@/types/candidate';
type DateRangeFilterProps = {
    value: CandidateDateRange;
    onChange: (range: CandidateDateRange) => void;
    className?: string;
};
const WEEKDAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const normalizeDate = (input: Date | null): Date | null => {
    if (!input) {
        return null;
    }
    const normalized = new Date(input);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};
const isSameDay = (left: Date | null, right: Date | null) => {
    if (!left || !right) {
        return false;
    }
    return (
        left.getFullYear() === right.getFullYear() &&
        left.getMonth() === right.getMonth() &&
        left.getDate() === right.getDate()
    );
};
const isWithinRange = (date: Date, range: CandidateDateRange) => {
    const { from, to } = range;
    if (!from || !to) {
        return false;
    }
    const time = date.getTime();
    return time >= from.getTime() && time <= to.getTime();
};
const getCalendarDays = (reference: Date) => {
    const monthStart = new Date(reference.getFullYear(), reference.getMonth(), 1);
    const startDay = (monthStart.getDay() + 6) % 7; // Monday as first day
    const firstVisible = new Date(monthStart);
    firstVisible.setDate(monthStart.getDate() - startDay);
    return Array.from({ length: 42 }).map((_, index) => {
        const date = new Date(firstVisible);
        date.setDate(firstVisible.getDate() + index);
        return date;
    });
};
const formatInputDate = (value: Date | null) => {
    if (!value) {
        return '';
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const parseInputDate = (value: string): Date | null => {
    if (!value) {
        return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
        return null;
    }
    return new Date(year, month - 1, day);
};
const DateRangeFilter = ({ value, onChange, className }: DateRangeFilterProps) => {
    const normalizedRange: CandidateDateRange = useMemo(
        () => ({
            from: normalizeDate(value.from),
            to: normalizeDate(value.to),
        }),
        [value.from, value.to]
    );
    const [visibleMonth, setVisibleMonth] = useState<Date>(
        () => normalizedRange.from ?? normalizedRange.to ?? new Date()
    );
    useEffect(() => {
        const target = normalizedRange.from ?? normalizedRange.to;
        if (target) {
            setVisibleMonth((current) => {
                if (
                    current.getFullYear() === target.getFullYear() &&
                    current.getMonth() === target.getMonth()
                ) {
                    return current;
                }
                return new Date(target.getFullYear(), target.getMonth(), 1);
            });
        }
    }, [normalizedRange.from, normalizedRange.to]);
    const days = useMemo(() => getCalendarDays(visibleMonth), [visibleMonth]);
    const handleDaySelect = (day: Date) => {
        const selected = normalizeDate(day);
        if (!selected) {
            return;
        }
        if (
            normalizedRange.from &&
            !normalizedRange.to &&
            selected.getTime() >= normalizedRange.from.getTime()
        ) {
            onChange({ from: normalizedRange.from, to: selected });
            return;
        }
        onChange({ from: selected, to: null });
    };
    const handlePrevMonth = () => {
        setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1));
    };
    const handleNextMonth = () => {
        setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1));
    };
    const handleInputChange = (key: 'from' | 'to', rawValue: string) => {
        const parsed = normalizeDate(parseInputDate(rawValue));
        if (key === 'from') {
            if (parsed && normalizedRange.to && parsed.getTime() > normalizedRange.to.getTime()) {
                onChange({ from: parsed, to: parsed });
                setVisibleMonth(parsed);
                return;
            }
            onChange({ from: parsed, to: normalizedRange.to });
            if (parsed) {
                setVisibleMonth(parsed);
            }
            return;
        }
        if (parsed && normalizedRange.from && parsed.getTime() < normalizedRange.from.getTime()) {
            onChange({ from: parsed, to: parsed });
            setVisibleMonth(parsed);
            return;
        }
        onChange({ from: normalizedRange.from, to: parsed });
        if (parsed) {
            setVisibleMonth(parsed);
        }
    };
    const isDayOutsideMonth = (day: Date, month: Date) =>
        day.getMonth() !== month.getMonth() || day.getFullYear() !== month.getFullYear();
    const renderDay = (day: Date) => {
        const isStart = isSameDay(day, normalizedRange.from);
        const isEnd = isSameDay(day, normalizedRange.to);
        const isRange = isWithinRange(day, normalizedRange);
        return (
            <button
                key={day.toISOString()}
                type="button"
                className={cnDayButton({
                    outside: isDayOutsideMonth(day, visibleMonth),
                    start: isStart,
                    end: isEnd,
                    inRange: isRange,
                })}
                onClick={() => handleDaySelect(day)}
            >
                <span className="relative z-10">{day.getDate()}</span>
            </button>
        );
    };
    return (
        <div
            className={className}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
        >
            <div className="flex items-center justify-between px-3 py-2">
                <Button variant="ghost" size="icon" className="size-7" onClick={handlePrevMonth}>
                    <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2 text-sm font-medium">
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                    {visibleMonth.toLocaleDateString('nl-NL', {
                        month: 'long',
                        year: 'numeric',
                    })}
                </div>
                <Button variant="ghost" size="icon" className="size-7" onClick={handleNextMonth}>
                    <ChevronRightIcon className="h-4 w-4" />
                </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 px-3 text-center text-xs font-medium text-slate-500">
                {WEEKDAY_LABELS.map((label) => (
                    <span key={label} className="py-1">
                        {label}
                    </span>
                ))}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-1 px-3 pb-3">
                {days.map((day) => renderDay(day))}
            </div>
            <div className="space-y-2 border-t border-slate-200 px-3 py-3">
                <label className="flex flex-col gap-1 text-sm">
                    <span className="text-xs text-slate-500">Van</span>
                    <input
                        type="date"
                        value={formatInputDate(normalizedRange.from)}
                        onChange={(event) => handleInputChange('from', event.target.value)}
                        className="w-full rounded border px-2 py-1 text-sm"
                    />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                    <span className="text-xs text-slate-500">Tot</span>
                    <input
                        type="date"
                        value={formatInputDate(normalizedRange.to)}
                        onChange={(event) => handleInputChange('to', event.target.value)}
                        className="w-full rounded border px-2 py-1 text-sm"
                    />
                </label>
            </div>
        </div>
    );
};
type DayButtonState = {
    outside: boolean;
    start: boolean;
    end: boolean;
    inRange: boolean;
};
const cnDayButton = ({ outside, start, end, inRange }: DayButtonState) => {
    const base =
        'relative flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary';
    if (start || end) {
        return `${base} bg-primary text-primary-foreground hover:bg-primary/90`;
    }
    if (inRange) {
        return `${base} bg-primary/10 text-primary hover:bg-primary/20`;
    }
    if (outside) {
        return `${base} text-slate-500/60 hover:bg-muted`;
    }
    return `${base} hover:bg-muted`;
};
export default DateRangeFilter;
